/**
 * Parses registration API 412 error responses and maps codes to user-friendly messages.
 * Supports both new errors array format and legacy field-based format for backward compatibility.
 *
 * Input: responseData from POST /api/v1/registrations (412)
 * Output: array of { field, code } for use in UI; getUserFriendlyMessage maps codes to display text
 */

const LEGACY_MESSAGE_TO_CODE = {
  'has already been taken': 'EMAIL_TAKEN',
  'is invalid': 'EMAIL_INVALID',
  'must be a valid email address': 'EMAIL_INVALID',
};

const CODE_TO_MESSAGE = {
  EMAIL_TAKEN: 'Email already in use. Sign In to access your account.',
  EMAIL_INVALID: 'Please enter a valid email address.',
  PASSWORD_TOO_SHORT: 'Password must be at least 8 characters.',
  PASSWORD_NO_DIGIT: 'Password must include at least one digit.',
};

const FALLBACK_MESSAGE = 'Registration could not be completed. Please try again.';

function inferCodeFromMessage(msg, field) {
  if (typeof msg !== 'string') return 'UNKNOWN';
  const lower = msg.toLowerCase();
  if (lower.includes('already been taken') || lower.includes('has already been taken')) {
    return 'EMAIL_TAKEN';
  }
  if (lower.includes('invalid') || lower.includes('valid email')) {
    return 'EMAIL_INVALID';
  }
  if (field === 'password' && lower.includes('character')) return 'PASSWORD_TOO_SHORT';
  if (field === 'password' && lower.includes('digit')) return 'PASSWORD_NO_DIGIT';
  return 'UNKNOWN';
}

/**
 * Parses API error response into normalized { field, code } array.
 * Prefers errors array when present; otherwise infers from legacy format.
 *
 * @param {object} responseData - Response body from 412 (errors.to_json or { errors: [...] })
 * @returns {Array<{field: string, code: string}>}
 */
export function parseRegistrationErrors(responseData) {
  if (!responseData || typeof responseData !== 'object') {
    return [];
  }

  const { errors } = responseData;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors
      .filter(e => e && typeof e.field === 'string' && typeof e.code === 'string')
      .map(e => ({ field: e.field, code: e.code }));
  }

  const parsed = [];
  const legacyKeys = ['email', 'password', 'first_name', 'last_name', 'full_name'];
  legacyKeys.forEach(field => {
    const messages = responseData[field];
    if (Array.isArray(messages)) {
      messages.forEach(msg => {
        const code = LEGACY_MESSAGE_TO_CODE[msg] || inferCodeFromMessage(msg, field);
        parsed.push({ field, code });
      });
    }
  });

  return parsed;
}

/**
 * Returns user-friendly message for a given error code.
 *
 * @param {string} code - Error code (e.g. EMAIL_TAKEN, EMAIL_INVALID)
 * @returns {string}
 */
export function getUserFriendlyMessage(code) {
  return CODE_TO_MESSAGE[code] || FALLBACK_MESSAGE;
}

/**
 * Returns true if parsed errors indicate email is already taken.
 *
 * @param {Array<{field: string, code: string}>} parsedErrors
 * @returns {boolean}
 */
export function isEmailTaken(parsedErrors) {
  return Array.isArray(parsedErrors) && parsedErrors.some(e => e.code === 'EMAIL_TAKEN');
}
